import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { announce } from '../helper/announcer';
import { TILE_COLORS } from './ColoredTiles';
var CELL = 110; // WORKSPACE_CELL_SIZE
// Hex → i18n color key (matches getCellAriaLabel in ColoredTiles).
var COLOR_NAME = {
    '#f73530': 'red',
    '#2a90f7': 'blue',
    '#1dac78': 'green',
    '#f4c500': 'yellow',
};
var buildCellCandidates = function (tile, t) {
    var _a, _b;
    var scale = (_a = tile.scale) !== null && _a !== void 0 ? _a : 1;
    var cell = CELL * scale;
    var left0 = tile.center.x - (tile.columns * cell) / 2;
    var top0 = tile.center.y - (tile.rows * cell) / 2;
    var out = [];
    for (var r = 0; r < tile.rows; r++) {
        for (var c = 0; c < tile.columns; c++) {
            var explicit = (_b = tile.partColors) === null || _b === void 0 ? void 0 : _b["".concat(r, "-").concat(c)];
            var raw = explicit !== null && explicit !== void 0 ? explicit : (tile.generationMode === 'random' ? TILE_COLORS[(r * 2 + c) % 4] : '');
            // Skip invisible / transparent cells (same rule as isCellNavSkippable).
            var skip = ((tile.generationMode === 'manual' || tile.generationMode === 'outline') && !explicit) ||
                (tile.generationMode === 'random' && raw === 'transparent');
            if (skip)
                continue;
            var colorKey = COLOR_NAME[raw];
            var colorName = colorKey ? t("text.".concat(colorKey)) : '';
            out.push({
                r: r,
                c: c,
                left: left0 + c * cell,
                top: top0 + r * cell,
                size: cell,
                label: colorName
                    ? t('text.coloredTilesCellColored', { row: r + 1, col: c + 1, color: colorName })
                    : t('text.coloredTilesCellEmpty', { row: r + 1, col: c + 1 }),
            });
        }
    }
    return out;
};
/**
 * Keyboard / VoiceOver cell picker for ColoredTiles. Mirrors LinkingCubePicker:
 * overlays a real <button> over every visible cell of the focused tile inside a
 * role="dialog". Activating a cell button opens that cell's context menu (the
 * Unsnap menu). Real buttons + dialog is the only pattern that reliably works
 * with VoiceOver here — in-place grid/roving approaches did not.
 */
var ColoredTilesCellPicker = function () {
    var _a;
    var t = useTranslation().t;
    var _b = useContext(DataContext), manipulative = _b.manipulative, setContextMenu = _b.setContextMenu;
    var _c = useState(null), tileId = _c[0], setTileId = _c[1];
    var containerRef = useRef(null);
    var firstBtnRef = useRef(null);
    var returnFocusIdRef = useRef(null);
    // Open listener — dispatched by the "Navigate Cell" context-menu action.
    // Also close the picker once an Unsnap actually happens (the extracted cell's
    // new tile takes over). Until then the picker stays mounted — opening a cell's
    // context menu must NOT close it, so the user can Exit the menu and land back
    // on the same cell.
    useEffect(function () {
        var onOpen = function (d) {
            setTileId(d.manipulativeId);
            returnFocusIdRef.current = d.manipulativeId;
            announce(t('text.coloredTilesCellNavigationEntered'));
        };
        var onUnsnap = function (d) {
            if (d.manipulativeId === returnFocusIdRef.current)
                setTileId(null);
        };
        customEventHandler.addEventListener('openColoredTilesCellPicker', onOpen);
        customEventHandler.addEventListener('coloredTilesUnsnapCell', onUnsnap);
        return function () {
            customEventHandler.removeEventListener('openColoredTilesCellPicker', onOpen);
            customEventHandler.removeEventListener('coloredTilesUnsnapCell', onUnsnap);
        };
    }, [t]);
    // Auto-focus the first cell button on open. 60ms beats closeMenu's 50ms
    // focus-restore timer so the picker wins focus.
    useEffect(function () {
        if (!tileId)
            return;
        var id = setTimeout(function () { var _a; return (_a = firstBtnRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 60);
        return function () { return clearTimeout(id); };
    }, [tileId]);
    var tile = tileId
        ? (_a = manipulative.find(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.id) === tileId; })) === null || _a === void 0 ? void 0 : _a.data
        : undefined;
    var candidates = tile ? buildCellCandidates(tile, t) : [];
    // Tear down if the tile vanished or has no visible cells.
    useEffect(function () {
        if (tileId && (!tile || candidates.length === 0))
            setTileId(null);
    }, [tileId, tile, candidates.length]);
    // Click-outside closes the picker — but ignore clicks inside the cell context
    // menu the picker just opened.
    useEffect(function () {
        if (!tileId)
            return;
        var onOutside = function (e) {
            var _a, _b;
            var target = e.target;
            if ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(target))
                return;
            if ((_b = target === null || target === void 0 ? void 0 : target.closest) === null || _b === void 0 ? void 0 : _b.call(target, '.shape-context-menu'))
                return;
            setTileId(null);
        };
        window.addEventListener('pointerdown', onOutside, { capture: true });
        return function () { return window.removeEventListener('pointerdown', onOutside, { capture: true }); };
    }, [tileId]);
    if (!tileId || !tile || candidates.length === 0)
        return null;
    var closePicker = function () { return setTileId(null); };
    var restoreFocus = function () {
        var id = returnFocusIdRef.current;
        if (!id)
            return;
        requestAnimationFrame(function () {
            var _a;
            var wrapper = document.querySelector("[data-manipulative-id=\"".concat(id, "\"]"));
            var target = (wrapper === null || wrapper === void 0 ? void 0 : wrapper.tabIndex) != null && wrapper.tabIndex >= 0
                ? wrapper
                : wrapper === null || wrapper === void 0 ? void 0 : wrapper.querySelector('[tabindex="0"], button, [role="button"]');
            (_a = (target !== null && target !== void 0 ? target : wrapper)) === null || _a === void 0 ? void 0 : _a.focus();
        });
    };
    var moveFocus = function (delta) {
        var _a, _b, _c;
        var btns = Array.from((_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('button.coloredTilesCellPicker__target')) !== null && _b !== void 0 ? _b : []);
        if (!btns.length)
            return;
        var cur = btns.indexOf(document.activeElement);
        var next = cur === -1 ? (delta > 0 ? 0 : btns.length - 1) : (cur + delta + btns.length) % btns.length;
        (_c = btns[next]) === null || _c === void 0 ? void 0 : _c.focus();
    };
    var onContainerKeyDown = function (e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            closePicker();
            restoreFocus();
        }
        else if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            moveFocus(1);
        }
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            moveFocus(-1);
        }
        // Enter / Space → native button click → onActivate.
    };
    return (_jsx("div", { ref: containerRef, className: "coloredTilesCellPicker", role: "dialog", "aria-modal": "true", "aria-label": t('text.coloredTilesCellPickerLabel'), onKeyDown: onContainerKeyDown, children: _jsx("div", { role: "application", "aria-label": t('text.coloredTilesCellPickerLabel'), style: { position: 'absolute', inset: 0 }, children: candidates.map(function (cand, idx) {
                // Open the cell's context menu (Unsnap / Exit). Keep the picker
                // MOUNTED — opening the menu must not close it. The menu's
                // "Exit"/Escape restores focus to this button (its trigger), so the
                // user lands back on the same cell. The picker only closes when an
                // Unsnap fires (see the onUnsnap listener) or on Escape / click-outside.
                // The existing `_cell_` ContextMenu branch shows Unsnap + Exit.
                var activate = function (btn) {
                    var synthId = "".concat(tileId, "_cell_").concat(cand.r, "_").concat(cand.c);
                    var rect = btn.getBoundingClientRect();
                    setContextMenu({ x: rect.right, y: rect.top, manipulativeId: synthId });
                };
                return (_jsx("button", { ref: idx === 0 ? firstBtnRef : undefined, type: "button", className: "coloredTilesCellPicker__target", style: {
                        position: 'absolute',
                        left: cand.left,
                        top: cand.top,
                        width: cand.size,
                        height: cand.size,
                        zIndex: 99999,
                    }, 
                    // VoiceOver (Mac / iPad) double-tap fires a synthetic click →
                    // opens the menu. Real keyboard / JAWS Enter is handled in
                    // onKeyDown below (which preventDefaults the synthesized click so
                    // the menu doesn't open twice).
                    onClick: function (e) { return activate(e.currentTarget); }, onKeyDown: function (e) {
                        if (e.key === 'Enter' || e.key === ' ') {
                            // preventDefault cancels the native click the browser would
                            // synthesize from this Enter, so activate() runs exactly once.
                            e.preventDefault();
                            e.stopPropagation();
                            activate(e.currentTarget);
                        }
                    }, "aria-label": cand.label }, "".concat(cand.r, "-").concat(cand.c)));
            }) }) }));
};
export default ColoredTilesCellPicker;
